import React, { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import {
  ChevronRight, ChevronDown, FolderOpen, MoreHorizontal, Plus,
  Pencil, Trash2, Share2, ListChecks, Table, CalendarCheck, AlertTriangle, Flag,
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

/**
 * RowMenu — reusable 3-dots dropdown displayed on the right of any sidebar
 * hierarchy row (CM, TL, Team, Project). Consumers pass an `items` array
 * with { label, icon, onClick, testId, divider?, danger? }.
 */
export function RowMenu({ items, testId }) {
  if (!items || items.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          className="p-1 opacity-0 group-hover:opacity-100 focus:opacity-100 data-[state=open]:opacity-100 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition"
          data-testid={testId}
          title="More options"
          type="button"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[190px]" onClick={(e) => e.stopPropagation()}>
        {items.map((it, i) => (
          <React.Fragment key={i}>
            {it.divider && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onSelect={(e) => { e.preventDefault(); it.onClick && it.onClick(); }}
              className={`gap-2 ${it.danger ? "text-red-600 focus:text-red-700 focus:bg-red-50" : ""}`}
              data-testid={it.testId}
            >
              {it.icon}
              <span>{it.label}</span>
            </DropdownMenuItem>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * QuickCreateButton — "+" button in sidebar rows for creating child lists.
 * Opens a small dropdown with "New Task List", "New Custom Table" and
 * optionally "New Publishing Schedule" (opt-in via `allowPublishing`).
 */
export function QuickCreateButton({ onCreate, testId, extraItems, allowPublishing }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
          className="p-1 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded transition opacity-70 hover:opacity-100"
          title="New Task List / New Custom Table"
          data-testid={testId}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {extraItems && extraItems.length > 0 && (
          <>
            {extraItems.map((item, i) => (
              <DropdownMenuItem key={i} onClick={item.onClick} data-testid={item.testId}>
                {item.icon} {item.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={() => onCreate("task_list")} data-testid={`${testId}-tasklist`}>
          <ListChecks className="h-4 w-4 mr-2 text-purple-500" /> New Task List
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => onCreate("custom_table")} data-testid={`${testId}-customtable`}>
          <Table className="h-4 w-4 mr-2 text-purple-500" /> New Custom Table
        </DropdownMenuItem>
        {allowPublishing && (
          <DropdownMenuItem onClick={() => onCreate("publishing_schedule")} data-testid={`${testId}-publishing`}>
            <CalendarCheck className="h-4 w-4 mr-2 text-emerald-500" /> New Publishing Schedule
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * TreeRow — generic tree row used for Dept / CM / TL / Team headers.
 * Supports inline rename, right-click context menu, quick-add button, and
 * an optional RowMenu populated by the caller.
 */
export function TreeRow({ testId, open, onToggle, icon, label, count, isEditing, editValue, onEditChange, onEditSave, onEditCancel, to, onContextMenu, onQuickCreate, allowPublishing, menuItems }) {
  const inputRef = useRef(null);
  useEffect(() => { if (isEditing && inputRef.current) inputRef.current.focus(); }, [isEditing]);
  return (
    <div className="group flex items-center gap-0.5 pr-1" data-testid={testId} onContextMenu={onContextMenu}>
      <button onClick={onToggle} className="p-1 rounded hover:bg-slate-100 text-slate-400" data-testid={`${testId}-chevron`}>
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </button>
      {to ? (
        <NavLink
          to={to}
          className={({isActive}) => `flex items-center gap-2 flex-1 min-w-0 px-1.5 py-1 rounded-md text-sm transition-colors ${isActive ? "bg-purple-50 text-purple-700 font-medium" : "text-slate-700 hover:bg-slate-50"}`}
          onClick={(e) => { if (isEditing) e.preventDefault(); }}
        >
          {icon}
          {isEditing ? (
            <input
              ref={inputRef}
              value={editValue}
              onChange={(e) => onEditChange(e.target.value)}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") onEditSave();
                if (e.key === "Escape") onEditCancel();
              }}
              onBlur={onEditSave}
              className="flex-1 text-sm border border-purple-500 rounded px-1 py-0 bg-white"
            />
          ) : (
            <span className="flex-1 text-left truncate">{label}</span>
          )}
          {!isEditing && typeof count === "number" && <span className="text-[10px] text-slate-400">{count}</span>}
        </NavLink>
      ) : (
        <div className="flex items-center gap-2 flex-1 min-w-0 px-1.5 py-1 rounded-md text-sm text-slate-700">
          {icon}
          {isEditing ? (
            <input ref={inputRef} value={editValue} onChange={(e) => onEditChange(e.target.value)}
              onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter") onEditSave(); if (e.key === "Escape") onEditCancel(); }}
              onBlur={onEditSave}
              className="flex-1 text-sm border border-purple-500 rounded px-1 py-0 bg-white" />
          ) : (
            <span className="flex-1 text-left truncate">{label}</span>
          )}
          {!isEditing && typeof count === "number" && <span className="text-[10px] text-slate-400">{count}</span>}
        </div>
      )}
      {!isEditing && <RowMenu items={menuItems} testId={`${testId}-menu`} />}
      {onQuickCreate && !isEditing && (
        <QuickCreateButton onCreate={onQuickCreate} testId={`${testId}-quick-add`} allowPublishing={allowPublishing} />
      )}
    </div>
  );
}

/**
 * ProjectRow — sidebar row for a Project under a TL / CM. Rendered red-tinted
 * with an "Unassigned" pill when TL or CM is missing.
 */
export function ProjectRow({ project, renaming, setRenaming, onSave, canEdit, onContextMenu, onQuickCreate, onShare, onDelete }) {
  const inputRef = useRef(null);
  const isEditing = renaming?.kind === "project" && renaming?.id === project.id;
  const isUnassigned = !(project.team_lead_ids && project.team_lead_ids.length > 0)
                       || !(project.content_manager_ids && project.content_manager_ids.length > 0);
  useEffect(() => { if (isEditing && inputRef.current) inputRef.current.focus(); }, [isEditing]);
  const projectMenuItems = canEdit ? [
    ...(onShare ? [{ label: "Share access…", icon: <Share2 className="h-3.5 w-3.5 text-purple-500" />, onClick: onShare, testId: `proj-menu-share-${project.id}` }] : []),
    { label: "Rename", icon: <Pencil className="h-3.5 w-3.5 text-slate-500" />, onClick: () => setRenaming({ kind: "project", id: project.id, value: project.name }), testId: `proj-menu-rename-${project.id}` },
    ...(onDelete ? [{ label: "Delete", icon: <Trash2 className="h-3.5 w-3.5" />, onClick: onDelete, danger: true, divider: true, testId: `proj-menu-delete-${project.id}` }] : []),
  ] : [];
  return (
    <div className="group flex items-center gap-1 pr-1" onContextMenu={onContextMenu}>
      <NavLink to={`/projects/${project.id}`} data-testid={`nav-project-${project.id}`}
        className={({isActive}) => {
          if (isUnassigned) return `flex items-center gap-2 flex-1 min-w-0 pl-2 pr-2 py-1 rounded-md text-sm transition-colors border ${isActive?"bg-red-100 text-red-800 font-medium border-red-300":"bg-red-50/60 text-red-700 border-red-200 hover:bg-red-100/70"}`;
          return `flex items-center gap-2 flex-1 min-w-0 pl-2 pr-2 py-1 rounded-md text-sm transition-colors ${isActive?"bg-purple-50 text-purple-700 font-medium":"text-slate-600 hover:bg-slate-50"}`;
        }}>
        <FolderOpen className={`h-3 w-3 shrink-0 ${isUnassigned ? "text-red-500" : ""}`} />
        {isEditing ? (
          <input
            ref={inputRef}
            value={renaming.value}
            onChange={(e) => setRenaming({ ...renaming, value: e.target.value })}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") onSave();
              if (e.key === "Escape") setRenaming(null);
            }}
            onBlur={onSave}
            className="flex-1 text-sm border border-purple-500 rounded px-1 py-0 bg-white"
          />
        ) : (
          <>
            <span className="truncate">{project.name}</span>
            {((project.urgent_count || 0) > 0 || (project.open_issue_count || 0) > 0 || isUnassigned) && (
              <span className="ml-auto shrink-0 flex items-center gap-1">
                {(project.urgent_count || 0) > 0 && (
                  <span
                    data-testid={`proj-urgent-pill-${project.id}`}
                    title={`${project.urgent_count} urgent ${project.urgent_count === 1 ? "task" : "tasks"}`}
                    className="inline-flex items-center gap-0.5 text-[8px] leading-3 uppercase tracking-wider bg-red-100 text-red-700 border border-red-200 px-1 py-px rounded-full font-bold"
                  >
                    <AlertTriangle className="h-2 w-2" />
                    {project.urgent_count} Urgent
                  </span>
                )}
                {(project.open_issue_count || 0) > 0 && (
                  <span
                    data-testid={`proj-issue-pill-${project.id}`}
                    title={`${project.open_issue_count} open task ${project.open_issue_count === 1 ? "issue" : "issues"}`}
                    className="inline-flex items-center gap-0.5 text-[8px] leading-3 uppercase tracking-wider bg-rose-500 text-white px-1 py-px rounded-full font-bold"
                  >
                    <Flag className="h-2 w-2" />
                    {project.open_issue_count} Task {project.open_issue_count === 1 ? "Issue" : "Issues"}
                  </span>
                )}
                {isUnassigned && (
                  <span className="text-[8px] leading-3 uppercase tracking-wider bg-red-500 text-white px-1 py-px rounded font-bold">Unassigned</span>
                )}
              </span>
            )}
          </>
        )}
      </NavLink>
      {!isEditing && <RowMenu items={projectMenuItems} testId={`proj-row-menu-${project.id}`} />}
      {onQuickCreate && !isEditing && (
        <QuickCreateButton onCreate={onQuickCreate} testId={`quick-add-project-${project.id}`} />
      )}
    </div>
  );
}

/**
 * SidebarListRow — nested list row (Task List / Custom Table / Publishing
 * Schedule) rendered underneath its parent CM / TL / Project.
 */
export function SidebarListRow({ list, active, onContextMenu, onQuickCreate }) {
  const Icon = list.kind === "task_list" ? ListChecks : (list.kind === "publishing_schedule" ? CalendarCheck : Table);
  const iconColor = list.kind === "task_list" ? "text-purple-500" : (list.kind === "publishing_schedule" ? "text-emerald-500" : "text-purple-500");
  return (
    <div
      className="group flex items-center gap-1 pr-1"
      data-testid={`sidebar-list-row-${list.id}`}
      onContextMenu={onContextMenu}
    >
      <NavLink
        to={list.kind === "publishing_schedule" ? `/publishing/${list.id}` : `/lists/${list.id}`}
        className={({ isActive }) => `flex items-center gap-2 flex-1 min-w-0 pl-2 pr-2 py-1 rounded-md text-sm transition-colors ${isActive || active ? "bg-purple-50 text-purple-700 font-medium" : "text-slate-600 hover:bg-slate-50"}`}
      >
        <Icon className={`h-3 w-3 shrink-0 ${iconColor}`} />
        <span className="truncate">{list.name}</span>
      </NavLink>
      {onQuickCreate && <QuickCreateButton onCreate={onQuickCreate} testId={`quick-add-list-${list.id}`} />}
    </div>
  );
}
